/**
 * Program Settings tab — edit loyalty program name, tiers, redemption, and expiry.
 *
 * Tiers map 1:1 to ERPNext Loyalty Program > collection_rules.
 * Each tier has: tier_name, min_spent (qualification threshold), collection_factor (฿ per point).
 *
 * Plan 1833 — Wizard -> Settings migration
 */

import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Plus, Trash2, ChevronDown, ChevronRight, Crown, icons } from "lucide-react";
import { GuidePopover, type GuideSlide } from "@/components/guide/GuidePopover";
import {
  IllustNameBadge,
  IllustNameEdit,
  IllustExpiryTimeline,
  IllustExpiryUrgency,
  IllustConversionRate,
  IllustConversionOff,
  IllustAutoJoin,
  IllustManualJoin,
  IllustTierLevels,
  IllustTierRates,
  IllustTierProgress,
} from "./guideIllustrations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useSetupLoyaltyProgram,
  useRewardsConfig,
  useDropdownOptions,
  type TierRow,
} from "../api/rewardsAdminHooks";
import { TierDetailView } from "./TierDetailView";

/* ── Guide slide content per setting ── */

const GUIDE_PROGRAM_NAME: GuideSlide[] = [
  {
    illustration: <IllustNameBadge />,
    title: "ชื่อโปรแกรมคืออะไร?",
    body: "ชื่อที่ลูกค้าจะเห็นในหน้าร้าน เช่น \"CSide Rewards\" หรือ \"สะสมแต้มร้านกาแฟ\" เลือกชื่อที่จำง่ายและสื่อถึงแบรนด์ของคุณ",
  },
  {
    illustration: <IllustNameEdit />,
    title: "เปลี่ยนชื่อได้ตลอด",
    body: "คุณสามารถเปลี่ยนชื่อโปรแกรมได้ทุกเมื่อ ลูกค้าจะเห็นชื่อใหม่ทันทีในครั้งถัดไปที่เปิดหน้าร้าน",
  },
];

const GUIDE_EXPIRY: GuideSlide[] = [
  {
    illustration: <IllustExpiryTimeline />,
    title: "แต้มหมดอายุทำงานอย่างไร?",
    body: "เมื่อเปิดใช้ แต้มที่ลูกค้าสะสมจะหมดอายุหลังจำนวนวันที่กำหนด นับจากวันที่ได้รับแต้ม",
  },
  {
    illustration: <IllustExpiryUrgency />,
    title: "ทำไมต้องตั้งวันหมดอายุ?",
    body: "กระตุ้นให้ลูกค้ากลับมาใช้แต้มเร็วขึ้น ช่วยเพิ่มยอดขายและลดภาระแต้มค้าง ร้านส่วนใหญ่ตั้งไว้ 90-365 วัน",
  },
];

const GUIDE_CONVERSION: GuideSlide[] = [
  {
    illustration: <IllustConversionRate />,
    title: "อัตราแลกแต้มคืออะไร?",
    body: "กำหนดว่า 1 แต้มมีมูลค่าเท่าไหร่เมื่อลูกค้านำไปแลกส่วนลด เช่น 1 แต้ม = 1 บาท หมายความว่าลูกค้ามี 100 แต้มก็แลกส่วนลดได้ 100 บาท",
  },
  {
    illustration: <IllustConversionOff />,
    title: "ตั้ง 0 คืออะไร?",
    body: "ถ้าตั้งเป็น 0 หมายความว่าปิดการแลกแต้ม ลูกค้ายังสะสมแต้มได้ แต่ยังไม่สามารถนำไปแลกส่วนลด เหมาะสำหรับช่วงเริ่มต้นที่ยังไม่พร้อมเปิดให้แลก",
  },
];

const GUIDE_AUTO_OPT_IN: GuideSlide[] = [
  {
    illustration: <IllustAutoJoin />,
    title: "สมัครสมาชิกอัตโนมัติ",
    body: "เมื่อเปิด ลูกค้าทุกคนที่ซื้อสินค้าจะเข้าร่วมโปรแกรมสะสมแต้มทันทีโดยไม่ต้องสมัครแยก",
  },
  {
    illustration: <IllustManualJoin />,
    title: "ถ้าปิดจะเป็นอย่างไร?",
    body: "ลูกค้าต้องสมัครสมาชิกด้วยตัวเองก่อนถึงจะเริ่มสะสมแต้มได้ เหมาะสำหรับโปรแกรมแบบ exclusive ที่ต้องการคัดกรองสมาชิก",
  },
];

const GUIDE_TIERS: GuideSlide[] = [
  {
    illustration: <IllustTierLevels />,
    title: "เทียร์สมาชิกคืออะไร?",
    body: "แบ่งลูกค้าตามยอดใช้จ่ายสะสม เช่น Silver, Gold, Platinum ยิ่งเทียร์สูง ยิ่งสะสมแต้มเร็วขึ้น",
  },
  {
    illustration: <IllustTierRates />,
    title: "อัตราสะสมแต้มของเทียร์",
    body: "\"ทุก ฿ เท่าไหร่ ได้ 1 แต้ม\" — เช่น Silver ทุก 100 บาทได้ 1 แต้ม แต่ Gold ทุก 50 บาทได้ 1 แต้ม ยิ่งเทียร์สูง ยิ่งได้แต้มเร็ว",
  },
  {
    illustration: <IllustTierProgress />,
    title: "ยอดใช้จ่ายขั้นต่ำ",
    body: "กำหนดว่าลูกค้าต้องใช้จ่ายสะสมเท่าไหร่ถึงจะเลื่อนเทียร์ เช่น Gold ต้องใช้จ่ายรวม 5,000 บาทขึ้นไป",
  },
];

const EMPTY_TIER: TierRow = {
  tier_name: "",
  min_spent: 0,
  collection_factor: 10,
};

const ALL_SENTINEL = "__all__";

export function ProgramSettingsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const configQuery = useRewardsConfig();
  const config = configQuery.data;
  const setupMutation = useSetupLoyaltyProgram();
  const dropdownQuery = useDropdownOptions();

  // Tier sub-page: ?tier=N navigates to the detail view for tier at index N
  const tierParam = searchParams.get("tier");
  const selectedTierIndex = tierParam !== null ? Number(tierParam) : null;

  const [programName, setProgramName] = useState("");
  const [expiryDays, setExpiryDays] = useState(365);
  const [neverExpire, setNeverExpire] = useState(false);
  const [conversionFactor, setConversionFactor] = useState(0);
  const [autoOptIn, setAutoOptIn] = useState(true);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [customerGroup, setCustomerGroup] = useState("");
  const [customerTerritory, setCustomerTerritory] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [tiers, setTiers] = useState<TierRow[]>([{ ...EMPTY_TIER }]);

  // Sync local state when config loads
  useEffect(() => {
    if (config) {
      setProgramName(config.loyalty_program_name || "Rewards");
      setExpiryDays(config.expiry_duration ?? 365);
      setNeverExpire(config.configured ? config.expiry_duration === 0 : false);
      setConversionFactor(config.conversion_factor ?? 0);
      setAutoOptIn(config.configured ? !!config.auto_opt_in : true);
      setFromDate(config.from_date ?? "");
      setToDate(config.to_date ?? "");
      setCustomerGroup(config.customer_group ?? "");
      setCustomerTerritory(config.customer_territory ?? "");
      if (config.tiers && config.tiers.length > 0) {
        setTiers(
          config.tiers.map((t) => ({
            tier_name: t.tier_name,
            min_spent: t.min_spent,
            collection_factor: t.collection_factor,
            tier_icon: t.tier_icon ?? null,
            tier_card_image: t.tier_card_image ?? null,
            tier_card_design: t.tier_card_design ?? null,
          })),
        );
      }
    }
  }, [config]);

  const addTier = useCallback(() => {
    setTiers((prev) => [...prev, { ...EMPTY_TIER }]);
  }, []);

  const removeTier = useCallback((idx: number) => {
    setTiers((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  }, []);

  const navigateToTier = useCallback(
    (idx: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("tier", String(idx));
        return next;
      });
    },
    [setSearchParams],
  );

  const navigateBackFromTier = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("tier");
      return next;
    });
  }, [setSearchParams]);

  const handleTierSave = useCallback(
    (idx: number, updated: TierRow) => {
      setTiers((prev) => {
        const next = [...prev];
        next[idx] = updated;
        return next;
      });
      // Save full program config (including updated tier) to backend
      if (!programName.trim()) return;
      const updatedTiers = [...tiers];
      updatedTiers[idx] = updated;
      setupMutation.mutate(
        {
          name: programName.trim(),
          expiry_days: neverExpire ? 0 : expiryDays,
          tiers: updatedTiers,
          conversion_factor: conversionFactor,
          auto_opt_in: autoOptIn ? 1 : 0,
          from_date: fromDate || undefined,
          to_date: toDate || undefined,
          customer_group: customerGroup || undefined,
          customer_territory: customerTerritory || undefined,
        },
        {
          onSuccess: () => {
            toast.success("บันทึกสำเร็จ");
          },
          onError: (err) => {
            toast.error(err.message || "เกิดข้อผิดพลาด กรุณาลองอีกครั้ง");
          },
        },
      );
    },
    [programName, tiers, neverExpire, expiryDays, conversionFactor, autoOptIn, fromDate, toDate, customerGroup, customerTerritory, setupMutation],
  );

  const isLoading = configQuery.isLoading || !config;

  const handleSave = () => {
    if (!programName.trim()) {
      toast.error("กรุณาตั้งชื่อโปรแกรม");
      return;
    }

    // Validate tiers
    for (const t of tiers) {
      if (!t.tier_name.trim()) {
        toast.error("กรุณาระบุชื่อเทียร์ทุกแถว");
        return;
      }
      if (t.collection_factor <= 0) {
        toast.error(`อัตราสะสมของเทียร์ "${t.tier_name}" ต้องมากกว่า 0`);
        return;
      }
    }

    setupMutation.mutate(
      {
        name: programName.trim(),
        expiry_days: neverExpire ? 0 : expiryDays,
        tiers,
        conversion_factor: conversionFactor,
        auto_opt_in: autoOptIn ? 1 : 0,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        customer_group: customerGroup || undefined,
        customer_territory: customerTerritory || undefined,
      },
      {
        onSuccess: () => {
          toast.success("บันทึกสำเร็จ");
        },
        onError: (err) => {
          toast.error(err.message || "เกิดข้อผิดพลาด กรุณาลองอีกครั้ง");
        },
      },
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4 pt-4">
        <Skeleton className="w-full h-64 rounded-2xl" />
      </div>
    );
  }

  // Tier detail sub-page
  if (
    selectedTierIndex !== null &&
    !isNaN(selectedTierIndex) &&
    selectedTierIndex >= 0 &&
    selectedTierIndex < tiers.length
  ) {
    return (
      <TierDetailView
        tier={tiers[selectedTierIndex]}
        tierIndex={selectedTierIndex}
        onBack={navigateBackFromTier}
        onSave={handleTierSave}
        isSaving={setupMutation.isPending}
      />
    );
  }

  return (
    <div className="space-y-6 pt-4">
      {/* Program info */}
      <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1 mb-3">
          ข้อมูลโปรแกรม
        </p>
        <Card className="rounded-2xl border-0">
          <CardContent className="divide-y px-5 py-7">
            <div className="space-y-2 pb-7">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="program-name">ชื่อโปรแกรม</Label>
                <GuidePopover slides={GUIDE_PROGRAM_NAME} />
              </div>
              <Input
                id="program-name"
                value={programName}
                onChange={(e) => setProgramName(e.target.value)}
                placeholder="เช่น CSide Rewards"
              />
            </div>

            <div className="space-y-3 py-7">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-1.5">
                    <Label>แต้มไม่มีวันหมดอายุ</Label>
                    <GuidePopover slides={GUIDE_EXPIRY} />
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {neverExpire
                      ? "แต้มจะสะสมได้ตลอด ไม่มีวันหมดอายุ"
                      : "แต้มจะหมดอายุหลังจำนวนวันที่กำหนด"}
                  </p>
                </div>
                <Switch checked={neverExpire} onCheckedChange={setNeverExpire} />
              </div>
              {!neverExpire && (
                <div className="flex items-center gap-3">
                  <Label className="text-sm text-muted-foreground whitespace-nowrap">หมดอายุหลัง</Label>
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    value={expiryDays}
                    onChange={(e) => setExpiryDays(Number(e.target.value))}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">วัน</span>
                </div>
              )}
            </div>

            <div className="space-y-2 py-7">
              <div className="flex items-center gap-1.5">
                <Label>อัตราแลกแต้ม (Conversion Factor)</Label>
                <GuidePopover slides={GUIDE_CONVERSION} />
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">1 แต้ม =</span>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={conversionFactor}
                  onChange={(e) =>
                    setConversionFactor(Math.max(0, Number(e.target.value) || 0))
                  }
                  className="w-28"
                />
                <span className="text-sm text-muted-foreground">฿</span>
              </div>
              <p className="text-xs text-muted-foreground">
                กำหนดมูลค่าของ 1 แต้มเมื่อลูกค้าใช้แต้มแลกส่วนลด (0 = ปิดการแลกแต้ม)
              </p>
            </div>

            <div className="flex items-center justify-between pt-7">
              <div className="space-y-0.5">
                <div className="flex items-center gap-1.5">
                  <Label>สมัครสมาชิกอัตโนมัติ</Label>
                  <GuidePopover slides={GUIDE_AUTO_OPT_IN} />
                </div>
                <p className="text-xs text-muted-foreground">
                  ลูกค้าใหม่จะเข้าร่วมโปรแกรมโดยอัตโนมัติ
                </p>
              </div>
              <Switch checked={autoOptIn} onCheckedChange={setAutoOptIn} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Advanced settings */}
      <div>
        <button
          type="button"
          onClick={() => setShowAdvanced((v) => !v)}
          className="flex items-center gap-1 text-xs font-medium text-muted-foreground uppercase tracking-wider px-1 mb-3 hover:text-foreground transition-colors"
        >
          <ChevronDown
            className={`h-3 w-3 transition-transform ${showAdvanced ? "rotate-0" : "-rotate-90"}`}
          />
          ตั้งค่าขั้นสูง
        </button>

        {showAdvanced && (
          <Card className="rounded-2xl border-0">
            <CardContent className="divide-y px-5 py-7">
              <div className="grid grid-cols-2 gap-4 pb-7">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    วันเริ่มโปรแกรม
                  </Label>
                  <Input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    วันสิ้นสุดโปรแกรม (ไม่บังคับ)
                  </Label>
                  <Input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-7">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    กลุ่มลูกค้า (ไม่บังคับ)
                  </Label>
                  <Select
                    value={customerGroup || ALL_SENTINEL}
                    onValueChange={(v) => setCustomerGroup(v === ALL_SENTINEL ? "" : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="ทุกกลุ่ม" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_SENTINEL}>ทุกกลุ่ม</SelectItem>
                      {dropdownQuery.data?.customer_groups?.map((g) => (
                        <SelectItem key={g} value={g}>
                          {g}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    เขตพื้นที่ (ไม่บังคับ)
                  </Label>
                  <Select
                    value={customerTerritory || ALL_SENTINEL}
                    onValueChange={(v) => setCustomerTerritory(v === ALL_SENTINEL ? "" : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="ทุกเขต" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_SENTINEL}>ทุกเขต</SelectItem>
                      {dropdownQuery.data?.territories?.map((t) => (
                        <SelectItem key={t} value={t}>
                          {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Tiers — summary list with drill-down */}
      <div>
        <div className="flex items-center justify-between px-1 mb-3">
          <div className="flex items-center gap-1.5">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              เทียร์สมาชิก
            </p>
            <GuidePopover slides={GUIDE_TIERS} />
          </div>
          <Button variant="outline" size="sm" onClick={addTier}>
            <Plus className="mr-1 h-3 w-3" />
            เพิ่มเทียร์
          </Button>
        </div>

        <Card className="rounded-2xl border-0">
          <CardContent className="divide-y px-0 py-0">
            {tiers.map((tier, idx) => (
              <div
                key={idx}
                className="group flex items-center gap-3 px-5 py-4 cursor-pointer hover:bg-muted/40 transition-colors"
                onClick={() => navigateToTier(idx)}
              >
                {/* Tier icon: Lucide name, image URL, or fallback */}
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted">
                  {tier.tier_icon && !tier.tier_icon.startsWith("/") && !tier.tier_icon.startsWith("http") && tier.tier_icon in icons ? (
                    (() => {
                      const Icon = icons[tier.tier_icon as keyof typeof icons];
                      return <Icon className="h-5 w-5 text-foreground" />;
                    })()
                  ) : tier.tier_icon ? (
                    <img
                      src={tier.tier_icon}
                      alt={tier.tier_name}
                      className="h-10 w-10 rounded-full object-cover"
                    />
                  ) : (
                    <Crown className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>

                {/* Info */}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">
                    {tier.tier_name || "เทียร์ใหม่"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    ขั้นต่ำ ฿{tier.min_spent.toLocaleString()} · ทุก
                    ฿{tier.collection_factor.toLocaleString()} ได้ 1 แต้ม
                  </p>
                </div>

                {/* Delete + chevron */}
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive opacity-0 group-hover:opacity-100 hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeTier(idx);
                    }}
                    disabled={tiers.length <= 1}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground px-1 mt-2">
          กดที่เทียร์เพื่อแก้ไขรายละเอียด รูปภาพ และตั้งค่าเพิ่มเติม
        </p>
      </div>

      {/* Save button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={setupMutation.isPending}
          className="min-h-[44px]"
        >
          {setupMutation.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          บันทึก
        </Button>
      </div>
    </div>
  );
}

